import { PersonaConfig, QueryType } from "../types";
import {
  ELECTRONICS_CUSTOMERS,
  ELECTRONICS_PERSONA_ID_PREFIX,
  getElectronicsCustomer,
  electronicsCustomerToPersona,
} from "./electronicsCustomers";
import {
  TSC_CUSTOMERS,
  TSC_PERSONA_ID_PREFIX,
  TscCustomer,
  getTscCustomer,
  tscCustomerToPersona,
} from "./tscCustomers";
import {
  KNITWELL_CUSTOMERS,
  KNITWELL_PERSONA_ID_PREFIX,
  getKnitwellCustomer,
  knitwellCustomerToPersona,
} from "./knitwellCustomers";
import {
  FINTECH_CUSTOMERS,
  FINTECH_PERSONA_ID_PREFIX,
  getFintechCustomer,
  fintechCustomerToPersona,
} from "./fintechCustomers";
import {
  FASHION_CUSTOMERS,
  FASHION_PERSONA_ID_PREFIX,
  getFashionCustomer,
  fashionCustomerToPersona,
} from "./fashionCustomers";
import {
  NOVA_CUSTOMERS,
  NOVA_PERSONA_ID_PREFIX,
  getNovaCustomer,
  novaCustomerToPersona,
} from "./novaCustomers";
import {
  VS_CUSTOMERS,
  VS_PERSONA_ID_PREFIX,
  getVsCustomer,
  vsCustomerToPersona,
} from "./vsCustomers";

// A demo "vertical" bundles everything the picker + call console need to run a
// brand: its seeded customers, the persona-id prefix, storefront chrome, and the
// per-profile "what you can test" hints. Adding a brand = one entry here + an
// AgentConfig. The call surface itself is shared (CallSurfaceStep).

export interface DemoOrder {
  orderId: string;
  product: string;
  brand?: string;
  category?: string;
  status: string;
  statusRaw?: string;
  totalUsd?: number;
  quantity?: number;
  carrier?: string;
  trackingNumber?: string;
  eta?: string;
  date?: string;
}

export interface DemoCustomer {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city?: string;
  state?: string;
  loyaltyTier: string;
  lifetimeValueBand?: string;
  preferredChannel?: string;
  crmNotes: string;
  ordersCount?: number;
  totalSpentUsd?: number;
  demoScenario?: string;
  scenarioNotes?: string;
  suggestedAlternative?: string;
  orders: DemoOrder[];
}

export interface TestHints {
  featured?: string;
  hints: string[];
}

/** A labelled row on the picker card. */
export interface CardField {
  label: string;
  value: string;
  accent?: boolean;
}

export interface VerticalConfig {
  slug: string;              // matches AgentConfig.slug
  brandName: string;         // "NuPlay Electronics" / "Tractor Supply"
  crmLabel: string;          // picker chip, e.g. "Tractor Supply · CRM"
  pickerBlurb: string;       // picker subtitle
  personaPrefix: string;
  storeUrl?: string;
  customers: DemoCustomer[];
  getById(id: string): DemoCustomer | undefined;
  toPersona(c: DemoCustomer): PersonaConfig;
  testHints(c: DemoCustomer): TestHints;
  queryForCustomer(c: DemoCustomer): QueryType;
  /**
   * Picker-card rows. Defaults to the retail set (product / order id / fulfilment),
   * which is meaningless outside a storefront — a payments vertical overrides it so
   * the card does not talk about "orders" and "delivery".
   */
  cardFields?(c: DemoCustomer): CardField[];
  /**
   * What this brand calls its loyalty/service band in the call console header.
   * "Plan" is right for a payments account and wrong for an apparel loyalty file.
   */
  tierLabel?: string;
  /** The three stat tiles in the call console's customer card. Defaults to spend/LTV/orders. */
  consoleStats?(p: PersonaConfig): { k: string; v: string }[];
}

// ─── Electronics (order-tracking / Shopify crew) ───────────────────────────────

const ELEC_FEATURED: Record<string, TestHints> = {
  "VE-20260545": { featured: "WISMO → cancel → save", hints: ["Cancel an in-flight order, then save it with a faster alternate product", "Prepaid refund + cancellation & new-order emails"] },
  "VE-20260536": { featured: "Pickup → cross-sell", hints: ["Confirm store-pickup readiness, then recommend a compatible charger", "Warm transfer to the store team"] },
  "VE-20260551": { featured: "Objection → loyalty", hints: ["Handle a “found it cheaper” objection with a delivery-fee explainer", "Convert to loyalty + one-time fee waiver"] },
};

function elecTestHints(c: DemoCustomer): TestHints {
  const feat = (c.orders ?? []).map((o) => ELEC_FEATURED[o.orderId]).find(Boolean);
  if (feat) return feat;
  const status = (c.orders?.[0]?.status ?? "").toLowerCase();
  const notes = (c.crmNotes ?? "").toLowerCase();
  const hints: string[] = [];
  if (status === "ready_for_pickup" || status === "pickup_processing") hints.push("Store-pickup readiness + accessory cross-sell", "Warm transfer to the store team");
  else if (status === "processing" || status === "label_created") hints.push("Order status (WISMO) lookup", "Cancellation allowed → refund + confirmation email");
  else if (status === "in_transit" || status === "out_for_delivery") hints.push("Live shipment tracking (WISMO)", "Cancellation blocked → alternate-product save");
  else if (status === "delayed") hints.push("Delayed-shipment explainer + reassurance", "Faster alternative / service recovery");
  else if (status === "exception") hints.push("Lost / exception package → escalation", "Apology token + warm handoff");
  else if (status === "delivered") hints.push("Post-delivery: returns & refund path", "Cancellation correctly blocked (already delivered)");
  else if (status === "cancelled" || status === "returned") hints.push("Refund status tracking", "Return eligibility + follow-up email");
  else hints.push("Order status (WISMO) lookup");
  if (hints.length < 2) {
    if (/price|cheaper|discount|expensive/.test(notes)) hints.push("Price objection → loyalty save");
    else if (/warrant|bundle|accessor/.test(notes)) hints.push("Warranty / accessory cross-sell");
    else if (/delay|shipping/.test(notes)) hints.push("Delay handling + service recovery");
    else if (c.loyaltyTier === "Platinum" || c.loyaltyTier === "Gold") hints.push("Loyalty-aware recovery & offers");
    else hints.push("Warm transfer to a human specialist");
  }
  return { hints: hints.slice(0, 2) };
}

// ─── Tractor Supply (Riley — TSC CX crew) ──────────────────────────────────────

// One card per customer (the TSC picker draws its own cards; this keeps the shared
// registry fed). Never scenario labels: the page shows customers, not test cases.
function tscTestHints(c: DemoCustomer): TestHints {
  const t = c as unknown as TscCustomer;
  return { featured: `Customer ${t.num}`, hints: [] };
}

function tscCardFields(c: DemoCustomer): CardField[] {
  const t = c as unknown as TscCustomer;
  return [
    { label: "Email", value: t.email },
    { label: "Billing zip", value: t.zip },
    { label: "Latest order", value: t.orders[0]?.orderId ?? "" },
  ].filter((f) => f.value);
}

function tscConsoleStats(p: PersonaConfig): { k: string; v: string }[] {
  const open = p.orders.filter((o) => o.status !== "delivered" && o.status !== "cancelled").length;
  return [
    { k: "Billing zip", v: p.postalCode ?? "" },
    { k: "Open orders", v: String(open) },
    { k: "Neighbor's Club", v: p.loyaltyTier ? "Member" : "No" },
  ];
}

// ─── Registry ──────────────────────────────────────────────────────────────────

const ELECTRONICS_VERTICAL: VerticalConfig = {
  slug: "order-tracking",
  brandName: "NuPlay Electronics",
  crmLabel: "NuPlay Electronics · CRM",
  pickerBlurb: "Search the CRM, then pick a profile — each card shows what you can test on the call.",
  personaPrefix: ELECTRONICS_PERSONA_ID_PREFIX,
  storeUrl: "https://nurix-electronics-2.myshopify.com",
  customers: ELECTRONICS_CUSTOMERS as DemoCustomer[],
  getById: (id) => getElectronicsCustomer(id) as DemoCustomer | undefined,
  toPersona: (c) => electronicsCustomerToPersona(c as never),
  testHints: elecTestHints,
  queryForCustomer: () => "wismo",
};

const TSC_VERTICAL: VerticalConfig = {
  slug: "tsc",
  brandName: "Tractor Supply",
  crmLabel: "Tractor Supply · Customers",
  pickerBlurb: "Pick a customer to call Riley as.",
  personaPrefix: TSC_PERSONA_ID_PREFIX,
  customers: TSC_CUSTOMERS as unknown as DemoCustomer[],
  getById: (id) => getTscCustomer(id) as unknown as DemoCustomer | undefined,
  toPersona: (c) => tscCustomerToPersona(c as never),
  testHints: tscTestHints,
  queryForCustomer: () => "cancellation",
  cardFields: tscCardFields,
  consoleStats: tscConsoleStats,
};

// ─── KnitWell / Ann Taylor (WISMO agent) ───────────────────────────────────────

const KNITWELL_VERTICAL: VerticalConfig = {
  slug: "knitwell",
  brandName: "Ann Taylor",
  crmLabel: "Ann Taylor · CRM",
  pickerBlurb: "Pick the test caller and start a call — Ava verifies the customer, then tracks their recent orders.",
  personaPrefix: KNITWELL_PERSONA_ID_PREFIX,
  customers: KNITWELL_CUSTOMERS as DemoCustomer[],
  getById: (id) => getKnitwellCustomer(id) as DemoCustomer | undefined,
  toPersona: (c) => knitwellCustomerToPersona(c as never),
  testHints: () => ({ hints: ["Where-is-my-order lookup across five recent orders", "Live shipment status + ETA"] }),
  queryForCustomer: () => "wismo",
};

// ─── Fintech (Claire — B2B invoices & payments) ────────────────────────────────

// What each caller is actually good for, keyed by scenario rather than by tier. An
// earlier version keyed these off the tier alone, which put "same-day re-draft, fee
// waived" on every Platinum card — including the two whose calls have nothing to do
// with a returned payment. On a picker whose whole job is telling a stranger which
// call to make, that is worse than no hint at all.
const FINTECH_SCENARIO_HINTS: Record<string, string[]> = {
  returned_payment:         ["A bank-returned draft with a distributor about to stop deliveries", "Push for the fee waiver and for a human — Platinum is the one tier that gets both"],
  integration_sync_failure: ["Invoice files not reaching Sage Intacct with month-end close on Friday", "Watch the re-transmit promise track the tier, not the urgency"],
  new_location_activation:  ["Onboarding a 12th property with three distributors to connect", "Activation timeline and fee both move with the tier"],
  gl_coding:                ["Custom GL coding — beer split bottle vs draft, spirits per property", "Not tier-sensitive, so the plan should never be mentioned"],
  missing_invoice:          ["A delivery signed for but never transmitted by the distributor", "Ask for a callback — Gold gets one, but never a live transfer"],
  cost_variance:            ["Wine up ~$6 a case plus unexpected split-case fees", "Claire should walk the variance, not adjudicate the dispute"],
  bank_account_change:      ["A bank switch with a draft already inside the verification window", "The honest answer is that the scheduled draft still hits the OLD account"],
  draft_timing:             ["Exact draft date and paying one distributor early", "Not tier-sensitive — the plan should not come up"],
  duplicate_draft:          ["Two identical ACH drafts on the same day", "Demand a same-day reversal — on Silver she must not promise one"],
  alcohol_compliance_hold:  ["A state delinquency stopping deliveries after a returned draft", "Push hard for a waiver and a manager — Silver gets neither"],
  portal_access:            ["Locked out of the portal, plus adding a bookkeeper", "No live credential reset, whatever the tier"],
  invoice_history_request:  ["An audit needing 18 months when the portal holds 15", "The correct answer is \"not entirely\" — export plus an archive request"],
};

// Only the three scripted demos are featured. Marking all twelve made the badge noise.
const FINTECH_FEATURED: Record<string, string> = {
  returned_payment:        "Demo 1 · Platinum",
  alcohol_compliance_hold: "Demo 2 · Silver",
  missing_invoice:         "Demo 3 · Gold",
};

function fintechTestHints(c: DemoCustomer): TestHints {
  const hints = FINTECH_SCENARIO_HINTS[c.demoScenario ?? ""] ?? [
    "Verify the caller (name + email), then read the service tier",
    "Answer from the knowledge base — no promise the tier doesn't cover",
  ];
  return { featured: FINTECH_FEATURED[c.demoScenario ?? ""], hints };
}

// The retail default talks about products, order ids and fulfilment. None of that
// exists here — a card should say what this business is and what is open on it.
function fintechCardFields(c: DemoCustomer): CardField[] {
  const business = (c.crmNotes || "").split(/ — |\. /)[0] ?? "";
  const open = c.orders ?? [];
  const flagged = open.filter((o) => o.status === "exception" || o.status === "delayed").length;
  return [
    { label: "Business", value: business },
    { label: "Email", value: c.email },
    { label: "Phone", value: c.phone },
    { label: "Account", value: `FTN-${c.id}` },
    { label: "Open items", value: open.length ? `${open.length} on the account` : "" },
    { label: "Needs attention", value: flagged ? `${flagged} flagged` : "None", accent: flagged > 0 },
  ].filter((f) => f.value);
}

function fintechQueryForCustomer(c: DemoCustomer): QueryType {
  switch (c.demoScenario) {
    case "returned_payment":         return "returned-payment";
    case "duplicate_draft":          return "returned-payment";
    case "alcohol_compliance_hold":  return "compliance-hold";
    case "missing_invoice":          return "missing-invoice";
    case "integration_sync_failure": return "integration-sync";
    default:                         return "returned-payment";
  }
}

const FINTECH_VERTICAL: VerticalConfig = {
  slug: "fintech",
  brandName: "Fintech",
  crmLabel: "Fintech · Client CRM",
  pickerBlurb:
    "Pick a client, then ask the same question of each — the service tier on the account decides what Claire is allowed to promise.",
  personaPrefix: FINTECH_PERSONA_ID_PREFIX,
  customers: FINTECH_CUSTOMERS as DemoCustomer[],
  getById: (id) => getFintechCustomer(id) as DemoCustomer | undefined,
  toPersona: (c) => fintechCustomerToPersona(c as never),
  testHints: fintechTestHints,
  queryForCustomer: fintechQueryForCustomer,
  cardFields: fintechCardFields,
};

// ─── NuPlay Fashion (Mira — 4-agent care crew) ──────────────────────────────────

// Keyed by SCENARIO, never by tier alone: a tier-keyed hint puts "instant refund" on every
// Icon card including the ones whose call has nothing to do with a refund, which is worse
// than no hint on a screen whose job is telling a stranger which call to make.
const FASHION_SCENARIO_HINTS: Record<string, string[]> = {
  refund_tier_contrast_icon:   ["A refund chased before it has landed", "Icon clears instantly — run this against the Member twin to see the tier decide"],
  refund_tier_contrast_member: ["The SAME refund question on the bottom tier", "Five to seven days, and no amount of pushing changes it"],
  exchange_cross_sell_icon:    ["A size exchange that turns into a styling conversation", "Watch the handoff to the stylist desk stay seamless"],
  icon_stylist_callback:       ["Styling help on the top tier", "Icon is the only tier with a dedicated stylist callback"],
  icon_price_adjustment:       ["Bought it, then it went on sale", "Price adjustment window is tier-sensitive"],
  insider_cancellation_save:   ["A cancellation the agent should try to save first", "Push twice — the save attempt must not become nagging"],
  insider_delivery_delay:      ["A parcel that is late with no explanation", "Ask what happens if it misses the weekend"],
  insider_pickup_ready:        ["A store-pickup order and how long it is held", "Ask about hours and the collection window"],
  insider_early_access:        ["Loyalty perks and early access to the sale", "Not tier-sensitive on timing — the plan should not come up"],
  member_pickup_hold_expired:  ["A pickup order whose hold has already lapsed", "The honest answer is not the comfortable one"],
  member_return_outside_window:["A return requested after the window closed", "Bottom tier, past the deadline — watch it stay warm while saying no"],
  member_wrong_item_complaint: ["Wrong item shipped, and a complaint about it", "Complaint plus return in one call — two things, one turn each"],
};
const FASHION_FEATURED: Record<string, string> = {
  "FSH-1001": "Demo 1 · Icon half",
  "FSH-1009": "Demo 1 · Member half",
  "FSH-1005": "Demo 2 · the save attempt",
};

function fashionTestHints(c: DemoCustomer): TestHints {
  return {
    featured: FASHION_FEATURED[c.id],
    hints: FASHION_SCENARIO_HINTS[c.demoScenario ?? ""] ?? [
      "Verify the caller, then read the loyalty tier",
      "Answer from the knowledge base — never a promise the tier does not cover",
    ],
  };
}

function fashionCardFields(c: DemoCustomer): CardField[] {
  const open = c.orders ?? [];
  const flagged = open.filter((o) => /delay|exception|hold|return|cancel/i.test(o.status)).length;
  return [
    { label: "Email", value: c.email },
    { label: "Phone", value: c.phone },
    { label: "Account", value: c.id },
    { label: "Orders", value: open.length ? `${open.length} on the account` : "" },
    { label: "Needs attention", value: flagged ? `${flagged} flagged` : "None", accent: flagged > 0 },
  ].filter((f) => f.value);
}

function fashionQueryForCustomer(c: DemoCustomer): QueryType {
  switch (c.demoScenario) {
    case "insider_delivery_delay":       return "wismo";
    case "insider_pickup_ready":
    case "member_pickup_hold_expired":   return "store-desk";
    case "insider_cancellation_save":    return "cancellation";
    case "exchange_cross_sell_icon":
    case "icon_stylist_callback":        return "product-rec";
    default:                             return "refund-status";
  }
}

const FASHION_VERTICAL: VerticalConfig = {
  slug: "nuplay-fashion",
  brandName: "NuPlay Fashion",
  crmLabel: "NuPlay Fashion · Customer CRM",
  pickerBlurb:
    "Pick a customer, then ask the same refund question of an Icon and a Member — the loyalty tier decides what Mira is allowed to promise.",
  personaPrefix: FASHION_PERSONA_ID_PREFIX,
  customers: FASHION_CUSTOMERS as unknown as DemoCustomer[],
  getById: (id) => getFashionCustomer(id) as unknown as DemoCustomer | undefined,
  toPersona: (c) => fashionCustomerToPersona(c as never),
  testHints: fashionTestHints,
  queryForCustomer: fashionQueryForCustomer,
  cardFields: fashionCardFields,
  tierLabel: "Tier",
};

// ─── Nova Money (Meera — 5-desk consumer finance crew) ─────────────────────────

const NOVA_SCENARIO_HINTS: Record<string, string[]> = {
  transaction_dispute:     ["A charge the caller does not recognise", "Prime gets provisional credit today; run the Basic twin to hear seven to ten days"],
  card_lost_stolen:        ["A lost card — blocking is allowed on the call", "Then ask to UNBLOCK it: that is never done on a call, however hard you push"],
  foreclosure_quote:       ["Closing a loan early and what it costs", "Prepayment fee waiver is segment-sensitive"],
  emi_missed_bounce:       ["An EMI that bounced", "Watch it stay factual about charges without lecturing"],
  card_limit_increase:     ["A credit-limit increase request", "Eligibility, not a promise — no outcome should be guaranteed"],
  failed_transfer_upi:     ["A UPI transfer that failed and the money is gone", "Auto-reversal timeline, stated as a fact"],
  refund_not_received:     ["A merchant refund that has not landed", "The honest answer involves the merchant's timeline, not just ours"],
  loan_application_status: ["A loan application mid-flight", "Next steps and what is still outstanding"],
  address_update:          ["Changing the address on the account", "What can be done on the call vs what needs the app"],
  kyc_reverification:      ["KYC pending and the account is limited", "Never ask for an OTP, a PIN or a full card number"],
};
const NOVA_FEATURED: Record<string, string> = {
  "NFX-C001": "Demo 1 · Prime half",
  "NFX-C009": "Demo 1 · Basic half",
  "NFX-C003": "Demo 2 · block yes, unblock no",
  "NFX-C002": "Demo 3 · loans, Prime",
  "NFX-C010": "Demo 3 · loans, Basic",
};

function novaTestHints(c: DemoCustomer): TestHints {
  return {
    featured: NOVA_FEATURED[c.id],
    hints: NOVA_SCENARIO_HINTS[c.demoScenario ?? ""] ?? [
      "Verify the caller, then read the segment",
      "Never an OTP, a PIN, or a full card number — last four only",
    ],
  };
}

function novaCardFields(c: DemoCustomer): CardField[] {
  const items = c.orders ?? [];
  const loans = items.filter((o) => /LN-|loan/i.test(o.orderId + o.product)).length;
  const cards = items.length - loans;
  return [
    { label: "Email", value: c.email },
    { label: "Phone", value: c.phone },
    { label: "Customer", value: c.id },
    { label: "Loans", value: loans ? String(loans) : "None" },
    { label: "Cards", value: cards ? String(cards) : "None" },
  ].filter((f) => f.value);
}

function novaQueryForCustomer(c: DemoCustomer): QueryType {
  switch (c.demoScenario) {
    case "transaction_dispute":
    case "card_lost_stolen":
    case "card_limit_increase":  return "returned-payment";
    case "kyc_reverification":
    case "address_update":       return "compliance-hold";
    case "failed_transfer_upi":
    case "refund_not_received":  return "missing-invoice";
    default:                     return "integration-sync";
  }
}

const NOVA_VERTICAL: VerticalConfig = {
  slug: "nova-money",
  brandName: "Nova Money",
  crmLabel: "Nova Money · Customer 360",
  pickerBlurb:
    "Pick a customer, then ask the same disputed-charge question of a Prime and a Basic — the segment decides how fast Meera can credit it back.",
  personaPrefix: NOVA_PERSONA_ID_PREFIX,
  customers: NOVA_CUSTOMERS as unknown as DemoCustomer[],
  getById: (id) => getNovaCustomer(id) as unknown as DemoCustomer | undefined,
  toPersona: (c) => novaCustomerToPersona(c as never),
  testHints: novaTestHints,
  queryForCustomer: novaQueryForCustomer,
  cardFields: novaCardFields,
  tierLabel: "Segment",
};

// ─── Victoria's Secret (Ava — 6-agent retail crew) ─────────────────────────────

const VS_SCENARIO_HINTS: Record<string, string[]> = {
  partial_delivery:      ["Two items arrived, the pink pyjama set is delayed — she needs it Friday", "Recovery compares pickup vs overnight; on pickup Ava warm-transfers the store to check the last unit before she travels"],
  defective_return:      ["A bra with a broken clasp; the app won't show a return option", "Assisted return + emailed details, THEN a tasteful Gold-event offer — never before"],
  gifting_concierge:     ["A birthday gift for her sister — light floral, under $100, by Saturday", "Two options with reasoning, wrap + card, price hidden on the invoice"],
  delivered_not_received:["Tracking says delivered to the front desk, but it isn't there", "Walks the checks, opens a case with full history, escalates — no auto-refund"],
  cancellation_denied:   ["Wants to cancel an order placed this morning", "It's already PICKING — the database refuses; Ava offers refuse-at-door / return, never fakes a cancel"],
};

// The five scripted demo callers get a "best demo" flag; run order 1 → 5 → 2.
const VS_FEATURED: Record<string, string> = {
  "CUST-0001": "Demo 1 · flagship — store transfer",
  "CUST-0005": "Demo 5 · guardrail — cancel denied",
  "CUST-0003": "Demo 2 · return + offer",
  "CUST-0002": "Demo 3 · gifting concierge",
  "CUST-0004": "Demo 4 · not received → escalation",
};

function vsTestHints(c: DemoCustomer): TestHints {
  return {
    featured: VS_FEATURED[c.id],
    hints: VS_SCENARIO_HINTS[c.demoScenario ?? ""] ?? [
      "Identify (phone or email), then verify with the other factor before anything account-specific",
      "Order lookup, returns, gifting — pick a scenario and drive it",
    ],
  };
}

function vsCardFields(c: DemoCustomer): CardField[] {
  const loc = [c.city, c.state].filter(Boolean).join(", ");
  const open = c.orders ?? [];
  const flagged = open.filter((o) => o.status === "delayed" || o.status === "exception").length;
  return [
    { label: "Email", value: c.email },
    { label: "Phone", value: c.phone },
    { label: "Location", value: loc },
    { label: "Orders", value: open.length ? `${open.length} on file` : "None" },
    { label: "Needs attention", value: flagged ? `${flagged} flagged` : "None", accent: flagged > 0 },
  ].filter((f) => f.value);
}

function vsQueryForCustomer(c: DemoCustomer): QueryType {
  switch (c.demoScenario) {
    case "partial_delivery":       return "wismo";
    case "delivered_not_received": return "wismo";
    case "defective_return":       return "refund-status";
    case "gifting_concierge":      return "product-rec";
    case "cancellation_denied":    return "cancellation";
    default:                       return "wismo";
  }
}

const VS_VERTICAL: VerticalConfig = {
  slug: "victorias-secret",
  brandName: "Victoria's Secret",
  crmLabel: "Victoria's Secret · Customer 360",
  pickerBlurb:
    "Every call starts anonymous — pick a customer, then watch Ava identify and verify them in two steps before she says a word about the account.",
  personaPrefix: VS_PERSONA_ID_PREFIX,
  storeUrl: "https://www.victoriassecret.com",
  customers: VS_CUSTOMERS as unknown as DemoCustomer[],
  getById: (id) => getVsCustomer(id) as unknown as DemoCustomer | undefined,
  toPersona: (c) => vsCustomerToPersona(c as never),
  testHints: vsTestHints,
  queryForCustomer: vsQueryForCustomer,
  cardFields: vsCardFields,
  tierLabel: "Tier",
};

// The multilingual TSC demo: same customers and cards, its own crew slug. Listed after
// TSC_VERTICAL, so a shared "tsc_" persona id still resolves through TSC_VERTICAL.
const TSC_MULTILINGUAL_VERTICAL: VerticalConfig = { ...TSC_VERTICAL, slug: "multilingual-support-agent" };
// Mike, the male-voice clone of the TSC crew: same customers and cards. Also after TSC_VERTICAL.
const TSC_MIKE_VERTICAL: VerticalConfig = { ...TSC_VERTICAL, slug: "tsc-mike", pickerBlurb: "Pick a customer to call Mike as." };

export const VERTICALS: VerticalConfig[] = [ELECTRONICS_VERTICAL, TSC_VERTICAL, TSC_MIKE_VERTICAL, TSC_MULTILINGUAL_VERTICAL, KNITWELL_VERTICAL, FINTECH_VERTICAL, FASHION_VERTICAL, NOVA_VERTICAL, VS_VERTICAL];

// Look up a vertical by agent slug; defaults to Electronics (the headline demo).
export function getVertical(slug?: string | null): VerticalConfig {
  return VERTICALS.find((v) => v.slug === slug) ?? ELECTRONICS_VERTICAL;
}

// Resolve a persona id (e.g. "tsc_CUST500006") to its persona via the owning
// vertical. Returns undefined if no vertical claims the prefix.
export function resolveVerticalPersona(personaId: string): PersonaConfig | undefined {
  for (const v of VERTICALS) {
    if (personaId.startsWith(v.personaPrefix)) {
      const c = v.getById(personaId.slice(v.personaPrefix.length));
      if (c) return v.toPersona(c);
    }
  }
  return undefined;
}
